import type { Map as MapLibreMap, StyleSpecification } from 'maplibre-gl';

import type { MapThemeName } from './utils/color-scale';

// MapLibre replicas of the published Revelio Mapbox Studio basemaps
// (tools/scripts/maps/exported-styles holds those). The live styles are deliberately flat —
// land, water, coastline and admin boundaries only, every symbol/road/terrain
// layer hidden — so the whole basemap reduces to five layers and needs no
// glyphs or sprites. Colors below are lifted verbatim from the published
// style JSONs.
//
// Tiles come from OpenFreeMap's keyless public instance (OpenMapTiles
// schema); its TileJSON carries the OpenStreetMap attribution. Swapping the
// provider (Stadia, self-hosted PMTiles) is a change to this one URL.
const OPENFREEMAP_TILEJSON_URL = 'https://tiles.openfreemap.org/planet';

export const MAPLIBRE_WATER_LAYER_ID = 'water';
const MAPLIBRE_COASTLINE_LAYER_ID = 'coastline';

interface BasemapPalette {
  land: string;
  water: string;
  admin0: string;
  admin1: string;
}

// Light: the Studio style layers land #f5f5f5 under country polygons
// #ededed, so visible land is #ededed everywhere country-boundaries has
// coverage (i.e. all land). OpenMapTiles has no country-polygon layer, so
// the background carries the land color directly — same rendered result,
// except lakes stay water-colored instead of being painted over.
const buildBasemapStyle = (
  palette: BasemapPalette,
  showStateBoundaries: boolean
): StyleSpecification => ({
  version: 8,
  sources: {
    basemap: {
      type: 'vector',
      url: OPENFREEMAP_TILEJSON_URL,
      // Overrides the TileJSON's "OpenFreeMap …" branding with the minimum
      // the licenses name: OSM data is ODbL, the OpenMapTiles schema is
      // CC-BY. OpenFreeMap itself asks for no credit.
      //
      // Nothing renders these now — no map mounts an AttributionControl — but
      // they stay as the record of what the tile source is.
      attribution:
        '<a href="https://www.openmaptiles.org/" target="_blank">© OpenMapTiles</a> <a href="https://www.openstreetmap.org/copyright" target="_blank">© OpenStreetMap contributors</a>',
    },
  },
  layers: [
    {
      id: 'land',
      type: 'background',
      paint: { 'background-color': palette.land },
    },
    {
      id: MAPLIBRE_WATER_LAYER_ID,
      type: 'fill',
      source: 'basemap',
      'source-layer': 'water',
      paint: {
        'fill-color': palette.water,
        'fill-outline-color': 'rgba(0, 0, 0, 0)',
      },
    },
    {
      id: MAPLIBRE_COASTLINE_LAYER_ID,
      type: 'line',
      source: 'basemap',
      'source-layer': 'water',
      filter: ['in', ['get', 'class'], ['literal', ['ocean', 'lake']]],
      paint: { 'line-color': palette.admin0 },
    },
    // `admin_level` here is the OSM level carried by the OpenMapTiles
    // `boundary` layer (country = 2, state/province = 4) — NOT the 0/1
    // Mapbox Streets uses for the same lines.
    {
      id: 'admin-0-boundary',
      type: 'line',
      source: 'basemap',
      'source-layer': 'boundary',
      filter: [
        'all',
        ['==', ['get', 'admin_level'], 2],
        ['==', ['get', 'maritime'], 0],
      ],
      paint: { 'line-color': palette.admin0 },
    },
    // Matches the published style's admin-1 layer: hidden at world zoom,
    // hard step to visible from z4.
    ...(showStateBoundaries
      ? ([
          {
            id: 'admin-1-boundary',
            type: 'line',
            source: 'basemap',
            'source-layer': 'boundary',
            minzoom: 2,
            filter: [
              'all',
              ['==', ['get', 'admin_level'], 4],
              ['==', ['get', 'maritime'], 0],
            ],
            paint: {
              'line-opacity': ['step', ['zoom'], 0, 4, 1],
              'line-color': palette.admin1,
            },
          },
        ] satisfies StyleSpecification['layers'])
      : []),
  ],
});

const PALETTES: Record<MapThemeName, BasemapPalette> = {
  light: {
    land: '#ededed',
    water: '#f2f2f2',
    admin0: '#c8c8ca',
    admin1: '#c8c8ca',
  },
  dark: {
    land: '#3b3e42',
    water: '#222429',
    admin0: '#25282d',
    admin1: '#25282d',
  },
};

// Precomputed so every caller with the same arguments gets the same object —
// the theme-swap effects compare style identity to decide whether to setStyle.
const buildStyles = (
  showStateBoundaries: boolean
): Record<MapThemeName, StyleSpecification> => ({
  light: buildBasemapStyle(PALETTES.light, showStateBoundaries),
  dark: buildBasemapStyle(PALETTES.dark, showStateBoundaries),
});

const STYLES_WITH_STATE_BOUNDARIES = buildStyles(true);
const STYLES_WITHOUT_STATE_BOUNDARIES = buildStyles(false);

/**
 * The OSM admin-1 lines are the state/province borders. The choropleth draws
 * its own outlines from the same tilesets as its fills, and the OSM geometry
 * is digitized differently, so keeping both doubles every shared border with
 * slightly offset lines. Only callers with no boundary layers of their own
 * (economic impact) keep them.
 */
export const getMaplibreBasemapStyle = (
  theme: MapThemeName,
  showStateBoundaries = true
): StyleSpecification =>
  (showStateBoundaries
    ? STYLES_WITH_STATE_BOUNDARIES
    : STYLES_WITHOUT_STATE_BOUNDARIES)[theme];

/**
 * The light and dark basemaps share structure and differ only in paint, so a
 * theme swap can be applied in place instead of via setStyle — a diffed
 * setStyle strips every runtime-added source/layer, forcing a full tile
 * refetch and re-parse of the choropleth.
 */
export const applyMaplibreBasemapTheme = (
  map: MapLibreMap,
  theme: MapThemeName,
  showStateBoundaries = true
): void => {
  const { layers } = getMaplibreBasemapStyle(theme, showStateBoundaries);
  const setPaintProperty = map.setPaintProperty.bind(map) as (
    layerId: string,
    name: string,
    value: unknown
  ) => void;
  for (const layer of layers) {
    if (!('paint' in layer) || !layer.paint || !map.getLayer(layer.id)) {
      continue;
    }
    for (const [name, value] of Object.entries(layer.paint)) {
      setPaintProperty(layer.id, name, value);
    }
  }
};
